import cx from 'classnames';
import { Field, Form, Formik } from 'formik';
import { format } from "date-fns";
import React, { useEffect, useState } from 'react';
import { Clock, Copy, Edit2, Info, Mail, Save, Trash2 } from 'react-feather';
import { connect, useDispatch } from 'react-redux';
import { withRouter } from 'react-router';
import AuthenticateChildren from '../../AuthenticateChildren';
import { AddButton } from '../../components/AddButton';
import { FeatherButton } from '../../components/FeatherButton';
import { Loader } from '../../components/Loader';
import { PTInputText, PTSwitch } from '../../components/PTElements';
import { notificationChore_Status, roles, scheduleTypes } from '../../constants';
import { EVO } from '../../EVO';
import * as actions from "../../redux/notificationchores";
import { urls } from '../../urls';
import './NotificationChores.css';

const TC = props => {
    useEffect(() => {
        props.fetchnotificationchores(props);
    }, []);

    const deleteNc = nc => {
        props.deleteItem(nc.id).then(() => {
            EVO.success('Deleted!');
            props.history.go();
        });
    }

    const renderTable = (chores, filter) => {
        return <div className="transactional-chores nc-table-wrapper">
            {filter.value === scheduleTypes.Recurring.value && <div>
                <div className="nc-title">Transactional Email Triggers</div>
                <div className="nc-subtitle">These triggers determine when your transactional emails send, and to who. You can then assign any of your transactional email templates created on the Send Email tab. Be sure to create an email template before you create the trigger for it. <a href="https://help.peptalkr.com.au/en/articles/3331060-transactional-emails" target="_blank" rel="noopener noreferrer">More info</a></div>
            </div>}
            {filter.value === scheduleTypes.OneOff.value && <div>
                <div className="nc-title">One-off Email Triggers</div>
            </div>}

            {EVO.any(chores) && <table className='table table-striped nc-table'>
                <thead>
                    <tr>
                        <th>Name</th>
                        <th className="thin">Status</th>
                        <th className="thin">Test</th>
                        <th className="thin">Details</th>
                        <th className="thin">Edit</th>
                        <th className="thin">Duplicate</th>
                        <AuthenticateChildren roles={[roles.Admin]} ignoreViewAs><th className="thin">Version</th></AuthenticateChildren>
                        <th className="thin">Delete</th>
                        {filter.value === scheduleTypes.OneOff.value && <th>Sent Date</th>}
                        {filter.value === scheduleTypes.OneOff.value && <th className="thin">Sent</th>}
                    </tr>
                </thead>
                <tbody>
                    {chores.map(notificationchore =>
                        <tr key={notificationchore.id}>
                            <td><EditName chore={notificationchore} /></td>
                            <td><StatusObj chore={notificationchore} /></td>
                            <td><SendTestEmail chore={notificationchore} /></td>
                            <td><FeatherButton feather={<Info />} to={`/email/${notificationchore.id}/history`}></FeatherButton></td>
                            <td>{notificationchore.status !== notificationChore_Status.InProgress && notificationchore.status !== notificationChore_Status.Queued && <FeatherButton feather={<Edit2 size={18} />} to={`/email/${notificationchore.id}`}></FeatherButton>}</td>
                            <td><FeatherButton feather={<Copy />} to={`/email/${notificationchore.id}/copy`}></FeatherButton></td>
                            <AuthenticateChildren roles={[roles.Admin]} ignoreViewAs><td><FeatherButton feather={notificationchore.versionCount || 0} to={`/sms/${notificationchore.id}/version`}></FeatherButton></td></AuthenticateChildren>
                            <td>{notificationchore.status !== notificationChore_Status.InProgress && notificationchore.status !== notificationChore_Status.Queued && <FeatherButton feather={<Trash2 />} onClick={() => deleteNc(notificationchore)} />}</td>
                            {filter.value === scheduleTypes.OneOff.value && <td>{notificationchore.sentDateTime && format(new Date(notificationchore.sentDateTime), 'dd/MM/yyyy HH:mm')}</td>}
                            {filter.value === scheduleTypes.OneOff.value && <td>{notificationchore.totalSent}</td>}
                        </tr>
                    )}
                </tbody>
            </table>}
            </div>
    }

    return (
        <div>
            <Loader loading={props.loading && !props.notificationchores}>
                {!props.error && <div>
                    {renderTable(props.transactionalChores, scheduleTypes.Recurring)}
                    {renderTable(props.transactionalOneOffChores, scheduleTypes.OneOff)}
                    <AddButton to={`/email/new`} />
                    </div>
                }
            </Loader>
        </div>
    );
}

const Status = ({ chore, ...props })=> {
    const [switchValue, setSwitchValue] = useState(chore.status === notificationChore_Status.Active);
    const dispatch = useDispatch();

    const setActive = () => {
        if (chore.scheduleTypeId === scheduleTypes.Recurring.value) {
            if (chore.status === notificationChore_Status.Paused || chore.status === notificationChore_Status.Active) {
                setSwitchValue(!switchValue);
                dispatch(actions.setActive(chore, !chore.active))
                    .then(() => {
                        EVO.success('Success!');
                    });
            }
        }
    }

    if (chore.status === notificationChore_Status.Paused || chore.status === notificationChore_Status.Active) {
        return <span><PTSwitch onChange={setActive} checked={switchValue} /></span>
    }


    return <span data-status={chore.status}
        className={cx("nc-status", { red: chore.status === notificationChore_Status.Paused || chore.status === notificationChore_Status.Draft || chore.status === notificationChore_Status.InsufficientCredits })}>
        {EVO.getPropertyNameFromValue(notificationChore_Status, chore.status).replace(/([A-Z])/g, " $1")}
    </span>
}

export const StatusObj = withRouter(Status);

export const SendTestEmail = ({ chore }) => {
    const [sendingEmail, setSendingEmail] = useState(false);

    const sendEmail = () => {
        if (sendingEmail)
            return;

        setSendingEmail(true);
        EVO.post('/api/notificationchores/test', { id: chore.id }).then(r => {
            let message = 'Test sent';
            if (r.email) {
                message += ' to ' + r.email;
            }
            EVO.success(message);
            setSendingEmail(false);
        }).catch(e => {
            setSendingEmail(false);
        });
    };

    return <FeatherButton onClick={sendEmail} feather={sendingEmail ? <Clock /> : <Mail />} />
}

const EditName = ({ chore }) => {
    const [show, setShow] = useState(false);
    const [loading, setLoading] = useState(false);
    const [name, setName] = useState(chore.name);

    const edit = () => {
        setShow(true);
    };

    const updateName = (values) => {
        setLoading(true);
        EVO.fetch(urls.api.notificationchores.updateName, { id: values.id, name: values.name }).then(r => {
            EVO.success('Successfully updated name');
            setShow(false);
            setLoading(false);
            setName(values.name);
        }).catch(e => {
            EVO.alert('Error updating name');
            setLoading(false);
        });
    };

    const initVal = {
        name: name,
        id: chore.id
    };

    return !show ? <span>{name}<FeatherButton feather={<Edit2 size={18} />} onClick={edit} classNames={'nc-title-edit-btn'} /></span> : <Formik
        initialValues={initVal}
        onSubmit={updateName}>
        {({ submitForm }) =>
            <Form>
                <div className="nc-title-edit">
                    <Field name="name" type="text" component={PTInputText} inline='true' left='true' autoFocus disabled={loading} />
                    <Loader loading={loading}><FeatherButton feather={<Save />} type="submit" /></Loader>
                </div>
            </Form>
        }
    </Formik>
};

const mapStateToProps = ({ notificationchores }) => ({
    notificationchores: notificationchores.notificationchores,
    loading: notificationchores.loading,
    error: notificationchores.error,
    activeLoading: notificationchores.activeLoading,
    deleteLoading: notificationchores.deleteLoading,
    transactionalChores: notificationchores.transactionalChores,
    transactionalOneOffChores: notificationchores.transactionalOneOffChores
});

export default connect(mapStateToProps, actions)(withRouter(TC));


// WEBPACK FOOTER //
// ./src/pages/NotificationChore/TransactionalChores.js